import { computed, toValue, type MaybeRefOrGetter } from 'vue'

import { type Module } from '@courses/types-layer/types/Module'
import { type Post as Lesson } from '@courses/types-layer/types/post'

import { useQueries, useQuery, useQueryClient } from '@tanstack/vue-query'

import { ModulesService } from '../services/modules.service'
import { buildModuleTree, buildModuleTreeLookUp } from '../utils/helper'

const MODULES_STALE_TIME_MS = 5 * 60 * 1000

export interface ModulesListQueryData {
  modules: Module[]
  totalModules: number
}
export interface ModuleLessonsQueryData {
  lessons: Lesson[]
  totalLessons: number
}

/**
 * Learner-scoped module APIs (`/courses/learners/.../modules`).
 * Catalog-preview curriculum: see {@link useCatalogOffersModuleTree}.
 */
export const useModulesQueryStore = (courseId: MaybeRefOrGetter<string>, locationId: MaybeRefOrGetter<string>) => {
  const service = new ModulesService()
  const queryClient = useQueryClient()

  const queryKeys = {
    all: (locId?: string, cId?: string) => ['modules', locId, cId] as const,
    list: (locId?: string, cId?: string) => ['modules', locId, cId, 'list'] as const,
    next: (locId?: string, cId?: string, moduleId?: string) => ['modules', locId, cId, 'next', moduleId] as const,
    lessons: (locId?: string, cId?: string, moduleId?: string) => ['modules', locId, cId, 'lessons', moduleId] as const,
  }

  const fetchAllModulesQuery = (extraEnabled?: MaybeRefOrGetter<boolean>) => {
    return useQuery<{ modules: Array<Module>; totalModules: number }, Error>({
      queryKey: computed(() => queryKeys.list(toValue(locationId), toValue(courseId))),
      queryFn: async () => await service.findAll(toValue(courseId), toValue(locationId)),
      staleTime: MODULES_STALE_TIME_MS,
      enabled: computed(() => {
        const extra = extraEnabled !== undefined ? toValue(extraEnabled) : true
        return !!toValue(locationId) && !!toValue(courseId) && extra
      }),
    })
  }

  const fetchNextModuleQuery = (moduleId: MaybeRefOrGetter<string>) => {
    return useQuery<any, Error>({
      queryKey: computed(() => queryKeys.next(toValue(locationId), toValue(courseId), toValue(moduleId))),
      queryFn: async () => await service.findNextModule(toValue(courseId), toValue(locationId), toValue(moduleId)),
      staleTime: MODULES_STALE_TIME_MS,
      enabled: computed(() => !!toValue(locationId) && !!toValue(courseId) && !!toValue(moduleId)),
    })
  }

  const fetchModuleLessonsQuery = (moduleId: MaybeRefOrGetter<string>) => {
    return useQuery<any, Error>({
      queryKey: computed(() => queryKeys.lessons(toValue(locationId), toValue(courseId), toValue(moduleId))),
      queryFn: async () => await service.findModuleLessons(toValue(courseId), toValue(locationId), toValue(moduleId)),
      staleTime: MODULES_STALE_TIME_MS,
      enabled: computed(() => !!toValue(locationId) && !!toValue(courseId) && !!toValue(moduleId)),
    })
  }

  const fetchModulesWithLessonsQuery = (modules: MaybeRefOrGetter<Module[] | undefined>, enabledModuleIds: MaybeRefOrGetter<string[]>) => {
    const queries = computed(() => {
      const resolvedModules = toValue(modules)
      if (!resolvedModules?.length) {
        return []
      }

      const cId = toValue(courseId)
      const locId = toValue(locationId)

      if (!cId || !locId) {
        return []
      }

      const enabledIds = toValue(enabledModuleIds)
      return resolvedModules.map(module => ({
        queryKey: queryKeys.lessons(locId, cId, module.id),
        queryFn: () => service.findModuleLessons(cId, locId, module.id),
        staleTime: MODULES_STALE_TIME_MS,
        enabled: enabledIds.includes(module.id),
      }))
    })

    return useQueries({
      queries,
      combine: results => {
        const resolvedModules = toValue(modules)

        const lessonResults = results.map(result => {
          if (result.isSuccess) {
            return result.data as { lessons: Lesson[]; totalLessons: number }
          }
          return undefined
        })

        const moduleTree = computed(() => buildModuleTree(resolvedModules, lessonResults))
        const contentLookUp = computed(() => buildModuleTreeLookUp(moduleTree.value))

        return {
          data: {
            moduleTree: moduleTree.value,
            contentLookUp: contentLookUp.value,
          },
          pending: results.some(result => result.isPending),
          isError: results.some(result => result.isError),
          isLoading: results.some(result => result.isLoading),
          error: results.find(result => result.isError)?.error,
        }
      },
    })
  }

  /** Imperative read for non-setup contexts (e.g. nav handlers); setup callers prefer `fetchAllModulesQuery`. */
  const ensureModulesList = async (): Promise<ModulesListQueryData> => {
    return await queryClient.ensureQueryData<ModulesListQueryData>({
      queryKey: queryKeys.list(toValue(locationId), toValue(courseId)),
      queryFn: () => service.findAll(toValue(courseId), toValue(locationId)),
      staleTime: MODULES_STALE_TIME_MS,
    })
  }

  /** Imperative single-module lessons read; same caveat as `ensureModulesList`. */
  const ensureModuleLessons = async (moduleId: string): Promise<ModuleLessonsQueryData> => {
    return await queryClient.ensureQueryData<ModuleLessonsQueryData>({
      queryKey: queryKeys.lessons(toValue(locationId), toValue(courseId), moduleId),
      queryFn: () => service.findModuleLessons(toValue(courseId), toValue(locationId), moduleId),
      staleTime: MODULES_STALE_TIME_MS,
    })
  }

  return {
    queryKeys,
    fetchAllModulesQuery,
    fetchNextModuleQuery,
    fetchModuleLessonsQuery,
    fetchModulesWithLessonsQuery,
    ensureModulesList,
    ensureModuleLessons,
  }
}
